import { noop } from 'lodash';
import { compact } from 'lodash';
import createReReSelector from 're-reselect';
import { createSelector } from 'reselect';

import { Shape2Icon } from '../../components/Shape2/Shape2';
import { SidebarLinksMenuLink } from '../../components/Sidebar/SidebarLinksMenu/SidebarLinksMenu';
import {
    SidebarMenuItem,
    SidebarMenuItems
} from '../../components/Sidebar/SidebarMenu/SidebarMenu';
import {
    DispatchProps,
    StateProps
} from '../../containers/SidebarContainer/SidebarContainer';
import {
    getSidebarLinks,
    getSidebarLinksHrefs,
    SidebarLinkKey,
    sidebarTitles
} from '../../declarations/sidebar/sidebar';
import { RoutesPath } from '../../routes/index';
import { LinkTarget } from '../../typings/common';
import { GdMenuItem } from '../../typings/graphql-grid';
import {
    AddEntityItem,
    SecondLevelSidebarMenuIds,
    SidebarDynamicItemsIds,
    SidebarItemsIds,
    SidebarLinkData,
    SidebarStaticItemsIds
} from '../../typings/sidebar';
import {
    CampaignAndGroupSelectorEntityToSelectType,
    State
} from '../../typings/state';
import { selectInfoLinks } from '../header/header';

const DEFAULT_LINKS: SidebarLinksMenuLink[] = [];

export const selectSidebarItemsIds = createSelector(
    (state: State) => state.commonData.accessRights,
    (state: State) => state.commonData.operatorFeatures,
    (state: State) =>
        selectSecondLevelSidebarLinksKeys(
            state,
            SecondLevelSidebarMenuIds.reportsMenu
        ).length,
    (state: State) =>
        selectSecondLevelSidebarLinksKeys(
            state,
            SecondLevelSidebarMenuIds.libraryMenu
        ).length,
    (state: State) => state.header.allowedMenuItems,
    (state: State) => state.router.route,
    (state: State) => state.commonData.operator.login,
    (state: State) => state.commonData.client.login,
    (
        access,
        features,
        reportsMenuLengthKeys,
        libraryMenuLengthKeys,
        allowedMenuItems: GdMenuItem[] = [],
        route: RoutesPath,
        userLogin: string,
        operatorLogin: string
    ): SidebarItemsIds => {
        const showCampsAvailable =
            allowedMenuItems.includes(GdMenuItem.SHOW_CAMPS) ||
            (userLogin && userLogin !== operatorLogin);

        const showClientsAvailable = [
            GdMenuItem.SHOW_AGENCY_CLIENTS,
            GdMenuItem.SHOW_MANAGER_CLIENTS
        ].some(el => allowedMenuItems.includes(el));

        const isEditPage = [
            RoutesPath.groupsEdit,
            RoutesPath.bannersEdit
        ].includes(route);

        const isRecommendationsAvailable = [
            RoutesPath.grid,
            RoutesPath.shortGrid,
            RoutesPath.log,
            RoutesPath.recommendationsItems,
            RoutesPath.recommendationsPage
        ].includes(route);

        const isGridAvailable = isRecommendationsAvailable;

        const { canCreateCampaignTypes } = access;

        return {
            static: compact([
                isGridAvailable &&
                    canCreateCampaignTypes.length &&
                    !isEditPage &&
                    SidebarStaticItemsIds.addEntity,

                isRecommendationsAvailable &&
                    !access.canEditInternalAds &&
                    allowedMenuItems.includes(GdMenuItem.RECOMMENDATIONS) &&
                    SidebarStaticItemsIds.goToRecommendations,

                showCampsAvailable && SidebarStaticItemsIds.goToCampaigns,
                !showCampsAvailable &&
                    showClientsAvailable &&
                    SidebarStaticItemsIds.goToShowClients,

                !access.canEditInternalAds &&
                    reportsMenuLengthKeys &&
                    SidebarStaticItemsIds.openReportsMenu,

                libraryMenuLengthKeys &&
                    !access.canEditInternalAds &&
                    SidebarStaticItemsIds.openLibraryMenu,

                SidebarStaticItemsIds.tools,

                SidebarStaticItemsIds.openInfoMenu
            ]),
            dynamic: compact([isEditPage && SidebarDynamicItemsIds.goToEdit])
        };
    }
);

export const selectSecondLevelSidebarLinksKeys = createReReSelector(
    (state: State) => state.commonData.operatorFeatures,
    (state: State) => state.commonData.accessRights,
    (state: State) => state.commonData.features.isMinusWordsLibEnabled,
    (state: State, selectedMenuId: SecondLevelSidebarMenuIds) => selectedMenuId,
    (
        operatorFeatures,
        accessRights,
        isMinusPhrasesLibraryEnabled,
        selectedMenuId: SecondLevelSidebarMenuIds
    ): SidebarLinkKey[] => {
        let result: Array<SidebarLinkKey>;

        switch (selectedMenuId) {
            case SecondLevelSidebarMenuIds.reportsMenu: {
                result = compact([
                    accessRights.canCheckCampaignsStats &&
                        'statistic-by-all-campaigns',
                    accessRights.canViewOfflineReports && 'order-reports',
                    accessRights.canMonitorPhrasesPositions &&
                        'phrases-positions-monitoring'
                ]);
                break;
            }
            case SecondLevelSidebarMenuIds.libraryMenu: {
                result = compact([
                    accessRights.canManageRetargertingConditions &&
                        'retargeting-and-audiences',
                    accessRights.canManageFeeds && 'feeds',
                    accessRights.canManageCreatives && 'creatives',
                    accessRights.canManageTurboLandings && 'turbo-pages',
                    accessRights.canViewMobileApps && 'mobile-apps',
                    isMinusPhrasesLibraryEnabled && 'minus-keywords-library'
                ]);
                break;
            }
            case SecondLevelSidebarMenuIds.infoMenu: {
                result = compact([
                    accessRights.canSeeAgencyClients && 'agency-clients',
                    accessRights.canSeeClientRepresentatives &&
                        'client-representatives',
                    accessRights.canEditInternationalization &&
                        'internationalization-interface',
                    accessRights.canUploadDocuments && 'upload-documents'
                ]);
                break;
            }

            default:
                result = [];
        }

        return result;
    }
)((state: State, selectedMenuId: SecondLevelSidebarMenuIds) => selectedMenuId);

export const selectSecondLevelSidebarLinksData = createSelector(
    (state: State) => state.commonData.login,
    (state: State) => state.commonData.client.ClientID,
    (state: State) =>
        (state.commonData.client.agencyInfo &&
            state.commonData.client.agencyInfo.login) ||
        '',
    (login = '', ClientID = '', agencyLogin = ''): SidebarLinkData => ({
        login,
        agencyLogin,
        ClientID
    })
);

export const selectSecondLevelSidebarLinks = createSelector(
    (state: State, selectedMenuId: SecondLevelSidebarMenuIds) => selectedMenuId,
    selectSecondLevelSidebarLinksKeys,
    selectSecondLevelSidebarLinksData,
    (
        selectedMenuId: SecondLevelSidebarMenuIds,
        sidebarLinksKeys: SidebarLinkKey[],
        sidebarLinksData: SidebarLinkData
    ): SidebarLinksMenuLink[] => {
        if (selectedMenuId === SecondLevelSidebarMenuIds.default) {
            return DEFAULT_LINKS;
        }

        const hrefs = getSidebarLinksHrefs(sidebarLinksData);
        const sidebarLinks = getSidebarLinks();

        return sidebarLinksKeys.reduce((acc: SidebarLinksMenuLink[], el) => {
            const href = hrefs[el];

            if (href) {
                acc.push({
                    ...sidebarLinks[el],
                    href
                });
            }

            return acc;
        }, []);
    }
);

export const selectSidebarInfoMenuLinks = createSelector(
    selectInfoLinks,
    links => {
        return links.map(el => {
            return {
                text: el.title,
                href: el.location ? el.location.toString() : '',
                target: LinkTarget.blank,
                isPureRoute: false
            };
        });
    }
);

export const selectSelectedSidebarItemIdByRoute = createSelector(
    (state: State) => state.router.route,
    (
        route: RoutesPath
    ): SidebarStaticItemsIds | SidebarDynamicItemsIds | undefined => {
        switch (route) {
            case RoutesPath.shortGrid:
            case RoutesPath.grid:
                return SidebarStaticItemsIds.goToCampaigns;

            case RoutesPath.recommendationsPage:
                return SidebarStaticItemsIds.goToRecommendations;

            case RoutesPath.tools:
                return SidebarStaticItemsIds.tools;

            case RoutesPath.groupsEdit:
            case RoutesPath.bannersEdit:
                return SidebarDynamicItemsIds.goToEdit;

            default:
                return undefined;
        }
    }
);

export const selectSidebarItems = createSelector(
    (stateProps: StateProps) => stateProps.selectedItemId,
    (stateProps: StateProps) => stateProps.items,
    (stateProps: StateProps) => stateProps.login,
    (stateProps: StateProps, dispatchProps: DispatchProps) => dispatchProps,
    (
        selectedItemId:
            | SidebarStaticItemsIds
            | SidebarDynamicItemsIds
            | undefined,
        items: SidebarItemsIds,
        login: string,
        dispatchProps: DispatchProps
    ): SidebarMenuItems => {
        const dict: Record<
            SidebarStaticItemsIds | SidebarDynamicItemsIds,
            Pick<SidebarMenuItem, 'icon' | 'onClick'>
        > = {
            [SidebarStaticItemsIds.addEntity]: {
                icon: Shape2Icon.plus,
                onClick: (): void =>
                    dispatchProps.processSidebarClick(
                        SidebarStaticItemsIds.addEntity
                    )
            },
            [SidebarStaticItemsIds.goToRecommendations]: {
                icon: Shape2Icon.sidebarRecommendations,
                onClick: (): void => dispatchProps.goToRecommendations(login)
            },
            [SidebarStaticItemsIds.goToCampaigns]: {
                icon: Shape2Icon.sidebarCampaigns,
                onClick: (): void => dispatchProps.goToCampaigns(login)
            },
            [SidebarStaticItemsIds.goToShowClients]: {
                icon: Shape2Icon.sidebarCampaigns,
                onClick: dispatchProps.goToShowClients
            },
            [SidebarStaticItemsIds.openReportsMenu]: {
                icon: Shape2Icon.sidebarReports,
                onClick: (): void =>
                    dispatchProps.processSidebarClick(
                        SidebarStaticItemsIds.openReportsMenu
                    )
            },
            [SidebarStaticItemsIds.openLibraryMenu]: {
                icon: Shape2Icon.sidebarLibrary,
                onClick: (): void =>
                    dispatchProps.processSidebarClick(
                        SidebarStaticItemsIds.openLibraryMenu
                    )
            },
            [SidebarStaticItemsIds.tools]: {
                icon: Shape2Icon.sidebarTools,
                onClick: (): void => dispatchProps.goToTools(login)
            },
            [SidebarStaticItemsIds.openInfoMenu]: {
                icon: Shape2Icon.sidebarInfo,
                onClick: (): void =>
                    dispatchProps.processSidebarClick(
                        SidebarStaticItemsIds.openInfoMenu
                    )
            },
            [SidebarDynamicItemsIds.goToEdit]: {
                icon: Shape2Icon.sidebarEdit
            }
        };

        const linkItems = [
            SidebarStaticItemsIds.goToRecommendations,
            SidebarStaticItemsIds.goToCampaigns,
            SidebarStaticItemsIds.tools
        ];

        return {
            static: items.static.map(
                (id: SidebarStaticItemsIds): SidebarMenuItem => {
                    const selected = id === selectedItemId;
                    const isLinkItem = linkItems.includes(id);

                    return {
                        id,
                        title: sidebarTitles[id],
                        selected,
                        ...dict[id],
                        onClick:
                            isLinkItem && selected ? noop : dict[id].onClick
                    };
                }
            ),
            dynamic: items.dynamic.map(
                (id: SidebarDynamicItemsIds): SidebarMenuItem => {
                    const selected = id === selectedItemId;

                    return {
                        id,
                        title: sidebarTitles[id],
                        selected,
                        ...dict[id]
                    };
                }
            )
        };
    }
);

export const selectAddEntityItems = createSelector(
    (state: State) => state.commonData.accessRights.canEditInternalAds,
    (canEditInternalAds: boolean): AddEntityItem[] => {
        return compact([
            CampaignAndGroupSelectorEntityToSelectType.CAMPAIGN,
            CampaignAndGroupSelectorEntityToSelectType.GROUP,
            CampaignAndGroupSelectorEntityToSelectType.BANNER,
            !canEditInternalAds &&
                CampaignAndGroupSelectorEntityToSelectType.PHRASE
        ]);
    }
);
